// src/web/CompletePayment.jsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import copyIcon from "../img/common/copy.png";
import infoIcon from "../img/home/payment.png";
import "../styles/main.scss";
// webviewBridge 유틸리티 import
import { sendToRN } from "../utils/webviewBridge.js";
// config import
import config from "../config/config.js";
// API import
import { httpGet, httpUrl } from "../api/httpClient.js";

// 금액 포맷
const toMoney = (v) => {
  const n = Number(String(v ?? "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? `${n.toLocaleString()}원` : (v ?? "-");
};

// studyroom / study_room 통합 확인
const isStudyRoomType = (type) => {
  const t = type?.toLowerCase();
  return t === "studyroom" || t === "study_room";
};

// passType별 표시명 매핑
const getPassTypeDisplayName = (passType) => {
  switch (passType?.toLowerCase()) {
    case "cash":
      return "캐시정기권";
    case "free":
      return "기간정기권(자유석)";
    case "fix":
      return "기간정기권(고정석)";
    case "1day":
      return "1일 이용권";
    case "locker":
      return "사물함";
    case "studyroom":
    case "study_room":
      return "스터디룸";
    default:
      return passType || "이용권";
  }
};

// 표시할 필드들 (passType에 따라 동적으로 변경)
const getDisplayFields = (passType) => {
  const fields = [
    { key: "storeName", label: "매장명" },
    { key: "passType", label: "이용권" },
    { key: "productInfo", label: "상품정보" },
    { key: "paymentAmount", label: "이용금액", isMoney: true },
  ];

  // ✅ 스터디룸: usageInfo(이용기간), usagePeriod(이용정보) 표시
  if (isStudyRoomType(passType)) {
    fields.push(
      { key: "usageInfo", label: "이용기간" },
      { key: "usagePeriod", label: "이용정보" },
    );
  }
  // 다른 이용권: validDays(이용기간) 표시
  else {
    fields.push({ key: "validDays", label: "이용기간" });
  }

  // 자유석일 때만 1일이용정보 표시
  if (passType?.toLowerCase() === "free") {
    fields.push({ key: "dailyUsableHours", label: "1일 이용정보" });
  }

  // 구분선
  fields.push({ type: "separator" });

  // 하단 정보
  fields.push(
    { key: "orderNumber", label: "주문번호" },
    { key: "paidAt", label: "결제일시" },
    { key: "paymentAmount", label: "결제금액", isMoney: true },
  );

  return fields;
};

// URL에서 orderNumber 및 aggregateId 추출
const getParamsFromQuery = () => {
  if (typeof window === "undefined")
    return { orderNumber: null, aggregateId: null };
  const q = new URLSearchParams(window.location.search);

  // 토스 결제 성공 후 리다이렉트에서 오는 파라미터들을 확인
  const orderNumber =
    q.get("orderNumber") ||
    q.get("orderId") ||
    q.get("order_id") ||
    q.get("paymentKey");
  const aggregateId = q.get("aggregateId") || q.get("id");

  console.log("[CompletePayment] URL 파라미터 추출:", {
    orderNumber,
    aggregateId,
    allParams: Object.fromEntries(q.entries()),
  });

  return { orderNumber, aggregateId };
};

export default function CompletePayment() {
  const navigate = useNavigate();
  const params = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState("");

  // 1) orderNumber 및 aggregateId 결정 (URL 우선, 없으면 sessionStorage에서 시도)
  const { orderNumber: urlOrderNumber, aggregateId: urlAggregateId } =
    useMemo(() => {
      // URL 경로에서 aggregateId 추출 (/complete-payment/:aggregateId)
      const pathAggregateId = params.aggregateId;
      const queryParams = getParamsFromQuery();

      return {
        orderNumber: queryParams.orderNumber,
        aggregateId: pathAggregateId || queryParams.aggregateId,
      };
    }, [params]);

  // ✅ parentToken 확인 (대리인 결제 여부)
  const parentToken = useMemo(() => {
    const q = new URLSearchParams(window.location.search);
    return (
      q.get("parentToken") || sessionStorage.getItem("parentToken") || null
    );
  }, []);

  // ✅ showEnterButton 확인 (앱에서 전달, 기본값 false)
  const showEnterButton = useMemo(() => {
    const q = new URLSearchParams(window.location.search);
    return q.get("showEnterButton") === "true";
  }, []);

  const orderNumber = useMemo(() => {
    if (urlOrderNumber) {
      return urlOrderNumber;
    }

    // sessionStorage에서 toss:draft 확인
    try {
      const draftStr = sessionStorage.getItem("toss:draft");
      if (draftStr) {
        const draft = JSON.parse(draftStr);
        return draft?.orderNumber || null;
      }
    } catch (e) {
      console.warn("[CompletePayment] sessionStorage parse error:", e);
    }

    return null;
  }, [urlOrderNumber]);

  // ✅ iOS 스와이프 뒤로가기 제스처 차단
  useEffect(() => {
    const preventSwipeBack = (e) => {
      // 화면 왼쪽 30px 이내에서 시작하는 터치 차단 (결제 페이지 보호)
      if (e.touches && e.touches[0] && e.touches[0].clientX < 30) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    document.addEventListener("touchstart", preventSwipeBack, {
      passive: false,
    });
    document.addEventListener("touchmove", preventSwipeBack, {
      passive: false,
    });

    return () => {
      document.removeEventListener("touchstart", preventSwipeBack);
      document.removeEventListener("touchmove", preventSwipeBack);
    };
  }, []);

  // 2) 결제 완료 데이터 로드 (URL 파라미터 우선, 필요시 RN에서 추가 데이터 요청)
  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (!orderNumber) {
        setErrMsg("주문번호를 찾을 수 없습니다.");
        setLoading(false);
        return;
      }

      // 1단계: URL 파라미터에서 기본 결제 정보 추출
      const urlParams = new URLSearchParams(window.location.search);

      console.log("[CompletePayment] 🔍 URL 전체 파라미터:", {
        url: window.location.href,
        allParams: Object.fromEntries(urlParams.entries()),
      });

      // 토스페이먼츠가 리다이렉트할 때 전달하는 파라미터들
      let orderId = urlParams.get("orderId"); // 토스가 돌려준 랜덤 문자열
      let paymentKey = urlParams.get("paymentKey");
      let amount = urlParams.get("amount")
        ? Number(urlParams.get("amount"))
        : null;

      // ✅ URL에 없으면 sessionStorage에서 가져오기 (대리인 결제 대응)
      if (!paymentKey || !amount) {
        try {
          const draftStr = sessionStorage.getItem("toss:draft");
          if (draftStr) {
            const draft = JSON.parse(draftStr);
            paymentKey = paymentKey || draft.paymentKey;
            amount = amount || draft.finalAmount || draft.amount;
            orderId = orderId || draft.orderId;
          }
        } catch (e) {
          console.warn(
            "[CompletePayment] sessionStorage toss:draft 파싱 실패:",
            e,
          );
        }
      }

      // sessionStorage에서 DB orderId (SK 서버 PK) 가져오기
      let orderIdSk = sessionStorage.getItem("dbOrderId");

      // tossOrderIdMapping에서도 확인 (fallback)
      const mappingStr = sessionStorage.getItem("tossOrderIdMapping");
      let mapping = null;
      if (mappingStr) {
        try {
          mapping = JSON.parse(mappingStr);
        } catch (e) {
          console.warn("[CompletePayment] tossOrderIdMapping 파싱 실패:", e);
        }
      }

      console.log("[CompletePayment] 🔍 토스에서 받은 원본 데이터:", {
        "토스가 돌려준 orderId": orderId,
        paymentKey: paymentKey,
        amount: amount,
        "orderNumber (우리가 관리)": orderNumber,
        "orderIdSk (sessionStorage)": orderIdSk,
        tossOrderIdMapping: mapping,
        "⚠️ orderIdSk 상태": orderIdSk ? "✅ 있음" : "❌ 없음 (문제!)",
        설명: {
          orderId: "토스가 리다이렉트할 때 전달 (랜덤 문자열)",
          orderIdSk: "SK 서버 orders 테이블 PK",
          orderNumber: "사용자용 주문번호",
        },
      });

      // ⚠️ orderIdSk가 없으면 경고
      if (!orderIdSk) {
        console.error(
          "[CompletePayment] ❌ CRITICAL: orderIdSk가 sessionStorage에 없습니다!",
        );
        console.error("[CompletePayment] sessionStorage 전체 내용:", {
          dbOrderId: sessionStorage.getItem("dbOrderId"),
          "toss:draft": sessionStorage.getItem("toss:draft"),
          tossOrderIdMapping: sessionStorage.getItem("tossOrderIdMapping"),
        });
      }

      const urlPaymentData = {
        orderNumber: orderNumber,
        paymentAmount: amount,
        paymentKey: paymentKey,
        orderId: orderId, // 토스가 돌려준 랜덤 문자열
        orderIdSk: orderIdSk, // SK 서버 orders 테이블 PK
        // ✅ URL에서 받은 aggregateId 추가
        aggregateId: urlAggregateId ? Number(urlAggregateId) : null,
        // 토스 결제 성공 시 기본 정보
        storeName: urlParams.get("storeName") || "매장",
        passType: urlParams.get("passType") || "cash",
        productInfo: urlParams.get("productName") || "상품",
        paidAt: new Date().toLocaleString("ko-KR"),
        couponAmount: 0,
      };

      console.log("[CompletePayment] 🔍 최종 urlPaymentData:", {
        ...urlPaymentData,
        "명확한 구분": {
          orderId: orderId + " (토스가 돌려준 랜덤 문자열)",
          orderIdSk: orderIdSk + " (SK 서버 PK)",
          orderNumber: orderNumber + " (사용자용)",
          aggregateId:
            urlPaymentData.aggregateId + " (이용권 ID - URL에서 받음)",
        },
      });

      // ✅ PaymentProcessing에서 이미 결제 완료 처리를 했으므로, 여기서는 API 호출하지 않음
      // aggregateId는 URL 파라미터로 이미 전달받음
      console.log(
        "[CompletePayment] ℹ️ PaymentProcessing에서 결제 완료 처리 완료 - API 재호출 없음",
      );

      // aggregateId를 sessionStorage에 저장 (QR 페이지에서 사용)
      if (urlPaymentData.aggregateId) {
        sessionStorage.setItem(
          "payment:aggregateId",
          String(urlPaymentData.aggregateId),
        );
        console.log(
          "[CompletePayment] ✅ aggregateId 저장:",
          urlPaymentData.aggregateId,
        );
      }

      // 2단계: sessionStorage에서 추가 정보 확인
      let sessionData = {};
      try {
        const draftStr = sessionStorage.getItem("toss:draft");
        if (draftStr) {
          const draft = JSON.parse(draftStr);
          console.log("[CompletePayment] 🔍 sessionStorage draft 원본:", {
            passKind: draft.passKind,
            passType: draft.passType,
            전체: draft,
          });
          sessionData = {
            storeName: draft.storeName || sessionData.storeName,
            passType: draft.passKind || draft.passType || sessionData.passType,
            productInfo: draft.productName || sessionData.productInfo,
            paymentAmount: draft.finalAmount || sessionData.paymentAmount,
            validDays: draft.validDays || "30일",
            usageInfo: draft.usageInfo || null,
            // ✅ dailyUsableHours 추가 (기간정기권 자유석용)
            dailyUsableHours: draft.dailyUsableHours || null,
            // ✅ usagePeriod 추가 (스터디룸용)
            usagePeriod: draft.usagePeriod || null,
          };
          console.log("[CompletePayment] 🔍 sessionData 변환 후:", {
            passType: sessionData.passType,
          });
        }

        // ✅ aggregateId를 sessionStorage에서 가져오기 (결제 승인 후 저장된 값)
        const savedAggregateId = sessionStorage.getItem("payment:aggregateId");
        if (savedAggregateId) {
          sessionData.aggregateId = Number(savedAggregateId);
          console.log(
            "[CompletePayment] ✅ sessionStorage에서 aggregateId 복원:",
            sessionData.aggregateId,
          );
        }
      } catch (e) {
        console.warn("[CompletePayment] sessionStorage 파싱 오류:", e);
      }

      // 3단계: 기본 데이터 병합
      const basicPaymentData = {
        ...urlPaymentData,
        ...sessionData,
        // 필수 필드 기본값 설정
        storeName: sessionData.storeName || urlPaymentData.storeName || "매장",
        passType: sessionData.passType || urlPaymentData.passType || "cash",
        productInfo:
          sessionData.productInfo || urlPaymentData.productInfo || "상품",
        paymentAmount:
          sessionData.paymentAmount || urlPaymentData.paymentAmount || 0,
        validDays: sessionData.validDays || "30일",
        usageInfo: sessionData.usageInfo || null,
        // ✅ dailyUsableHours 명시적으로 병합 (기간정기권 자유석용)
        dailyUsableHours:
          sessionData.dailyUsableHours ||
          urlPaymentData.dailyUsableHours ||
          null,
        // ✅ usagePeriod 명시적으로 병합 (스터디룸용)
        usagePeriod:
          sessionData.usagePeriod || urlPaymentData.usagePeriod || null,
        // ✅ aggregateId 명시적으로 병합 (우선순위: urlPaymentData > sessionData)
        aggregateId:
          urlPaymentData.aggregateId || sessionData.aggregateId || null,
      };

      console.log("[CompletePayment] 병합된 결제 데이터:", basicPaymentData);
      console.log("[CompletePayment] 🔍 aggregateId 병합 상태:", {
        "urlPaymentData.aggregateId": urlPaymentData.aggregateId,
        "sessionData.aggregateId": sessionData.aggregateId,
        "basicPaymentData.aggregateId": basicPaymentData.aggregateId,
      });

      // 4단계: 기본 데이터가 충분하면 바로 표시
      if (basicPaymentData.orderNumber) {
        let finalPaymentData = {
          ...basicPaymentData,
          aggregateId:
            basicPaymentData?.aggregateId || basicPaymentData?.id || null,
          paymentAmount: basicPaymentData.paymentAmount || 50000,
        };

        // ✅ 스터디룸이고 usageInfo/usagePeriod가 없으면 getOrderInfo API로 보완
        const isStudyRoom = isStudyRoomType(finalPaymentData.passType);
        const missingUsageInfo = !finalPaymentData.usageInfo || finalPaymentData.usageInfo === '-';
        const missingUsagePeriod = !finalPaymentData.usagePeriod || finalPaymentData.usagePeriod === '-';

        if (isStudyRoom && (missingUsageInfo || missingUsagePeriod)) {
          try {
            console.log("[CompletePayment] 스터디룸 usageInfo/usagePeriod 보완 API 호출:", basicPaymentData.orderNumber);
            const orderInfoRes = await httpGet(httpUrl.getOrderInfo(basicPaymentData.orderNumber), [], {}, true);
            console.log("[CompletePayment] getOrderInfo 응답:", orderInfoRes);

            if (orderInfoRes?.code === 100 && orderInfoRes?.result) {
              const orderData = orderInfoRes.result;
              if (orderData.usageInfo) finalPaymentData.usageInfo = orderData.usageInfo;
              if (orderData.usagePeriod) finalPaymentData.usagePeriod = orderData.usagePeriod;
              console.log("[CompletePayment] ✅ usageInfo/usagePeriod 보완 완료:", {
                usageInfo: finalPaymentData.usageInfo,
                usagePeriod: finalPaymentData.usagePeriod,
              });
            }
          } catch (apiError) {
            console.warn("[CompletePayment] getOrderInfo 호출 실패 (기본 데이터 사용):", apiError);
          }
        }

        console.log("[CompletePayment] 최종 데이터 설정:", finalPaymentData);
        console.log("[CompletePayment] ⚠️ aggregateId 확인:", {
          aggregateId: finalPaymentData.aggregateId,
          orderNumber: finalPaymentData.orderNumber,
          구분: "aggregateId는 이용권 ID, orderNumber는 주문번호",
        });
        setData(finalPaymentData);
        setLoading(false);
        return;
      }

      // 5단계: RN에서 추가 데이터 요청
      try {
        const rnPaymentData = await sendToRN(
          "REQUEST_PAYMENT_COMPLETE",
          { orderNumber },
          15000,
        );

        if (mounted) {
          const finalData = {
            ...basicPaymentData,
            ...rnPaymentData,
            orderNumber: orderNumber, // orderNumber는 항상 유지
            aggregateId: rnPaymentData?.aggregateId,
          };
          setData(finalData);
          setLoading(false);
        }
      } catch (error) {
        console.warn(
          "[CompletePayment] RN 데이터 요청 실패, 기본 데이터 사용:",
          error,
        );
        if (mounted) {
          // RN 요청 실패해도 기본 데이터로 표시
          const fallbackData = {
            ...basicPaymentData,
            aggregateId: basicPaymentData?.aggregateId,
          };
          setData(fallbackData);
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [orderNumber, parentToken, urlAggregateId]);

  // 3) 홈 탭으로 이동 (RN 브리지 → 웹 폴백)
  const goHome = () => {
    try {
      const payload = { action: "GO_HOME_TAB", payload: {} };
      if (
        typeof window !== "undefined" &&
        typeof window.__askRN === "function"
      ) {
        window.__askRN(payload.action, payload.payload);
        return;
      }
      if (
        typeof window !== "undefined" &&
        window.ReactNativeWebView &&
        typeof window.ReactNativeWebView.postMessage === "function"
      ) {
        window.ReactNativeWebView.postMessage(JSON.stringify(payload));
        return;
      }
      // 웹 폴백: 루트로 이동
      if (typeof window !== "undefined") {
        window.location.replace(`${window.location.origin}/`);
      }
    } catch (e) {}
  };

  // 4) 좌석 선택 화면으로 이동 (캐시정기권, 자유석)
  const goSelectSeat = () => {
    try {
      console.log("[CompletePayment] GO_SELECT_SEAT 액션 전송");

      const payload = {
        orderNumber: data.orderNumber || orderNumber,
        aggregateId: data.aggregateId || data.id,
        storeName: data.storeName,
        passType: data.passType,
      };

      console.log("[CompletePayment] GO_SELECT_SEAT payload:", payload);

      // RN 브리지로 좌석 선택 화면 이동 요청
      if (typeof window.__askRN === "function") {
        window.__askRN("GO_SELECT_SEAT", payload);
        return;
      }

      if (
        window.ReactNativeWebView &&
        typeof window.ReactNativeWebView.postMessage === "function"
      ) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            type: "GO_SELECT_SEAT",
            payload: payload,
          }),
        );
        return;
      }

      console.warn("[CompletePayment] RN 브리지를 찾을 수 없습니다");
    } catch (error) {
      console.error("[CompletePayment] GO_SELECT_SEAT 전송 실패:", error);
    }
  };

  // 5) QR 페이지로 이동 (1일이용권, 고정석, 사물함, 스터디룸)
  const goQr = () => {
    if (!data) {
      console.warn(
        "[CompletePayment] 결제 데이터가 없어서 QR 페이지로 이동할 수 없습니다.",
      );
      return;
    }

    const finalOrderNumber = data.orderNumber || orderNumber;
    // ✅ aggregateId는 서버에서 받은 값만 사용 (orderNumber로 폴백하지 않음)
    const finalAggregateId =
      urlAggregateId || data.aggregateId || data.id || null;

    console.log("[CompletePayment:goQr] aggregateId 결정:", {
      urlAggregateId,
      dataAggregateId: data.aggregateId,
      dataId: data.id,
      finalOrderNumber,
      finalAggregateId,
      "⚠️ 주의": "aggregateId는 이용권 ID (필수), orderNumber는 주문번호",
    });

    // ✅ aggregateId가 없으면 에러 표시
    if (!finalAggregateId) {
      console.error(
        "[CompletePayment] ❌ aggregateId가 없어서 QR 페이지로 이동할 수 없습니다!",
      );
      alert("이용권 정보를 불러오지 못했습니다.\n잠시 후 다시 시도해주세요.");
      return;
    }

    // ✅ React Native WebView 환경인지 확인
    if (window.ReactNativeWebView || typeof window.__askRN === "function") {
      console.log(
        "[CompletePayment] ✅ RN WebView 환경 - postMessage로 QR 페이지 요청",
      );

      const payload = {
        storeId: window.SKYSUNNY?.storeId || data.storeId,
        storeName: data.storeName || window.SKYSUNNY?.storeName,
        address: data.address || window.SKYSUNNY?.address,
        aggregateId: finalAggregateId,
        orderNumber: finalOrderNumber,
        passType: data.passType,
        passName: data.productInfo || data.passName,
        type: data.passType,
        // ✅ 스터디룸일 때 상품정보를 usageSeat으로 전달
        ...(isStudyRoomType(data.passType) && {
          usageSeat: data.productInfo || data.passName,
        }),
      };

      console.log("[CompletePayment] SHOW_QR payload:", payload);

      // RN 브리지로 QR 페이지 이동 요청
      try {
        if (typeof window.__askRN === "function") {
          window.__askRN("SHOW_QR", payload);
          return;
        }

        if (
          window.ReactNativeWebView &&
          typeof window.ReactNativeWebView.postMessage === "function"
        ) {
          window.ReactNativeWebView.postMessage(
            JSON.stringify({
              action: "SHOW_QR",
              payload: payload,
            }),
          );
          return;
        }
      } catch (error) {
        console.error("[CompletePayment] SHOW_QR 전송 실패:", error);
      }

      return;
    }

    // ✅ 웹 환경일 때만 페이지 이동
    console.log("[CompletePayment] 웹 환경 - 페이지 이동");

    // QR 페이지에서 사용할 데이터를 미리 준비
    const qrPayload = {
      // QR 코드 기본 정보 (실제 API 응답과 유사한 구조로 구성)
      qrData: {
        // ✅ 스터디룸일 때는 상품정보를 usageSeat으로 사용
        usageSeat:
          isStudyRoomType(data.passType)
            ? data.productInfo || data.passName || null
            : data.seatName || data.seatNumber || null,
        wifiId: data.wifiId || window.SKYSUNNY?.wifiSsid || null,
        wifiPassword:
          data.wifiPassword || window.SKYSUNNY?.wifiPassword || null,
        entryPassword:
          data.entryPassword ||
          data.entrancePassword ||
          window.SKYSUNNY?.entryPassword ||
          window.SKYSUNNY?.entrancePassword ||
          null,
        imageUrl: null, // QR 이미지는 API에서 받아와야 함
      },
      orderDetails: {
        storeName: data.storeName || window.SKYSUNNY?.storeName || null,
        passType: data.passType || null,
        productInfo: data.productInfo || null,
      },
      attachedInfo: {
        usageInfo: data.usageInfo || data.validDays || null,
        expireText: data.expireText || null,
        remainingInfo: data.remainingInfo || null,
      },
      qrIdentifier: {
        orderId: finalOrderNumber,
        passId: null,
        aggregateId: finalAggregateId,
        timestamp: Math.floor(Date.now() / 1000) + 30 * 60, // 30분 후 만료
      },
    };

    // sessionStorage에 QR 데이터 저장 (QR 페이지에서 사용)
    try {
      sessionStorage.setItem("qr:payload", JSON.stringify(qrPayload));
    } catch (e) {
      console.error("[CompletePayment] sessionStorage 저장 실패:", e);
    }

    // QrCode.js가 기대하는 파라미터 준비
    const finalToken =
      window.SKYSUNNY?.accessToken ||
      window.SKYSUNNY?.token ||
      localStorage.getItem("accessToken") ||
      "";
    const finalStoreId = window.SKYSUNNY?.storeId || "";

    const searchParams = {
      aggregateId: String(finalAggregateId || ""),
      token: finalToken,
      storeId: String(finalStoreId),
    };

    // 빈 값 제거 (QR 페이지에서 파라미터 파싱 시 문제 방지)
    Object.keys(searchParams).forEach((key) => {
      if (
        !searchParams[key] ||
        searchParams[key] === "undefined" ||
        searchParams[key] === "null"
      ) {
        delete searchParams[key];
      }
    });

    // React Router navigate를 사용하여 이동 (SPA 방식)
    try {
      const searchString = new URLSearchParams(searchParams).toString();
      const qrPath = `/qr-code?${searchString}`;
      navigate(qrPath);
    } catch (error) {
      console.error(
        "[CompletePayment] React Router navigate 실패, 폴백 사용:",
        error,
      );

      // 폴백: window.location 사용
      const p = new URLSearchParams(searchParams);
      const qrUrl = `${window.location.origin}/qr-code?${p.toString()}`;
      window.location.href = qrUrl;
    }
  };

  // 6) 입장하기 버튼 클릭 핸들러 (passType에 따라 분기)
  const handleEnterClick = () => {
    if (!data) {
      console.warn("[CompletePayment] 결제 데이터가 없습니다.");
      return;
    }

    const passType = data.passType?.toLowerCase();

    console.log("[CompletePayment] 입장하기 클릭 - 전체 데이터:", {
      passType: data.passType,
      passTypeLower: passType,
      data: data,
    });

    // 캐시정기권(cash) 또는 자유석(free)일 때는 GO_SELECT_SEAT
    // ✅ 한글 passType도 처리 ("캐시 이용권", "기간정기권 (자유석)" 등)
    const isCashOrFree =
      passType === "cash" ||
      passType === "free" ||
      passType?.includes("캐시") ||
      passType?.includes("자유석");

    if (isCashOrFree) {
      console.log("[CompletePayment] ✅ 좌석 선택 화면으로 이동:", passType);
      goSelectSeat();
    }
    // 1일이용권(1day), 고정석(fix), 사물함(locker), 스터디룸(studyroom)일 때는 QR 페이지
    else {
      console.log("[CompletePayment] ✅ QR 페이지로 이동:", passType);
      goQr();
    }
  };

  // 5) 로딩/에러 UI
  if (loading) {
    return (
      <div className="complete-container">
        <img src={infoIcon} alt="payment" className="payment-img" />
        <div className="notice-box">
          <span className="notice-text font-bm">
            결제 정보를 불러오는 중입니다...
          </span>
        </div>
      </div>
    );
  }
  if (errMsg) {
    return (
      <div className="complete-container container">
        <img src={infoIcon} alt="payment" className="payment-img" />
        <div className="notice-box">
          <span className="notice-text font-bm">{errMsg}</span>
        </div>
        <div className="checkout-footer-fixed" style={{ background: '#F6F6F6' }}>
          <div className="bottom-button" style={{ margin: 0 }}>
            <button onClick={goHome}>닫기</button>
          </div>
        </div>
      </div>
    );
  }

  // 6) 정상 렌더
  return (
    <div
      className="complete-container container"
      style={{ overscrollBehaviorX: "none", touchAction: "pan-y" }}
    >
      {/* 이미지 */}
      <img src={infoIcon} alt="payment" className="payment-img" />

      {/* 결제 완료 안내 */}
      <div className="notice-box">
        <span className="notice-text font-bm">결제가 완료되었습니다.</span>
      </div>

      {/* 정보 카드 */}
      <div className="info-card">
        {getDisplayFields(data?.passType).map((field, index) => {
          if (field.type === "separator") {
            return <div key={index} className="line"></div>;
          }

          let value = data[field.key];

          // dailyUsableHours는 여러 경로에서 확인
          if (field.key === "dailyUsableHours") {
            value =
              data?.dailyUsableHours ||
              data?.selectedTicket?.dailyUsableHours ||
              null;
          }

          let displayValue = field.isMoney ? toMoney(value) : value || "-";

          // passType 필드인 경우 표시명으로 변환
          let finalValue =
            field.key === "passType"
              ? getPassTypeDisplayName(value)
              : displayValue;

          // dailyUsableHours 필드인 경우 "시간" 단위 추가
          if (field.key === "dailyUsableHours" && value) {
            finalValue = `${value}시간`;
          }

          return (
            <div key={index} className="info-row">
              <span className="title">{field.label}</span>
              <span className="text">{finalValue}</span>
            </div>
          );
        })}

        {/* 쿠폰할인은 모든 passType에서 표시 (값이 있을 때만) */}
        {!!data.couponAmount && (
          <div className="info-row">
            <span className="title">쿠폰할인</span>
            <span className="text">-{toMoney(data.couponAmount)}</span>
          </div>
        )}
      </div>

      {/* 입장하기 - 대리인 결제가 아니고 showEnterButton이 true일 때만 표시 */}
      {!parentToken && showEnterButton && (
        <div
          className={`enter-btn-box ${
            isStudyRoomType(data.passType) ? "studyroom-spacing" : ""
          }`}
        >
          <button className="enter-btn" onClick={handleEnterClick}>
            입장하기
          </button>
        </div>
      )}

      {/* 스터디룸일 때만 QR 공유 안내 표시 (대리인 결제 제외) */}
      {!parentToken && isStudyRoomType(data.passType) && (
        <div className="section2-title-box3">
          <p className="note-text font-bm">
            함께 이용할 친구에게 QR 링크를 공유하세요.
          </p>
          <div
            className="copy-url-box"
            onClick={async () => {
              try {
                // ✅ aggregateId 확인 (여러 경로에서 시도)
                const aggregateId =
                  data.aggregateId ||
                  data.id ||
                  sessionStorage.getItem("payment:aggregateId") ||
                  null;

                console.log("[CompletePayment] URL 복사 - aggregateId 확인:", {
                  "data.aggregateId": data.aggregateId,
                  "data.id": data.id,
                  sessionStorage: sessionStorage.getItem("payment:aggregateId"),
                  "data 전체": data,
                  "최종 aggregateId": aggregateId,
                });

                if (!aggregateId) {
                  console.error("[CompletePayment] aggregateId가 없습니다");
                  console.error("[CompletePayment] data 객체:", data);
                  alert(
                    "QR 링크를 생성할 수 없습니다.\n잠시 후 다시 시도해주세요.",
                  );
                  return;
                }

                // ✅ aggregateId만 포함된 깨끗한 URL 생성 (환경별 BASE_URL 사용)
                const fullUrl = `${config.BASE_URL}/qr-code/${aggregateId}`;

                console.log("[CompletePayment] 복사 시도:", fullUrl);
                console.log("[CompletePayment] BASE_URL:", config.BASE_URL);

                // 복사 시도 (폴백 포함)
                let copySuccess = false;

                // 방법 1: navigator.clipboard API (최신 방법)
                if (navigator.clipboard && navigator.clipboard.writeText) {
                  try {
                    await navigator.clipboard.writeText(fullUrl);
                    copySuccess = true;
                    console.log(
                      "[CompletePayment] ✅ clipboard API로 복사 성공",
                    );
                  } catch (clipboardErr) {
                    console.warn(
                      "[CompletePayment] clipboard API 실패, 폴백 시도:",
                      clipboardErr,
                    );
                  }
                }

                // 방법 2: execCommand 폴백 (구형 브라우저 지원)
                if (!copySuccess) {
                  try {
                    const textArea = document.createElement("textarea");
                    textArea.value = fullUrl;
                    textArea.style.position = "fixed";
                    textArea.style.left = "-999999px";
                    textArea.style.top = "-999999px";
                    document.body.appendChild(textArea);
                    textArea.focus();
                    textArea.select();

                    const successful = document.execCommand("copy");
                    document.body.removeChild(textArea);

                    if (successful) {
                      copySuccess = true;
                      console.log(
                        "[CompletePayment] ✅ execCommand로 복사 성공",
                      );
                    }
                  } catch (execErr) {
                    console.error(
                      "[CompletePayment] execCommand 실패:",
                      execErr,
                    );
                  }
                }

                if (copySuccess) {
                  alert("링크가 복사되었습니다.");
                  console.log("[CompletePayment] QR 링크 복사됨:", fullUrl);
                  console.log("[CompletePayment] aggregateId:", aggregateId);
                } else {
                  throw new Error("모든 복사 방법 실패");
                }
              } catch (err) {
                console.error("[CompletePayment] 링크 복사 실패:", err);
                alert("링크 복사에 실패했습니다.\n수동으로 복사해주세요.");
              }
            }}
            style={{ cursor: "pointer" }}
          >
            <span className="font-noto url-text">
              {/* 화면에 표시되는 짧은 URL */}
              {`http://skasca.me/qr/${data.aggregateId}`}
            </span>
            <img src={copyIcon} alt="copy" className="icon14" />
            <span className="copy-btn">URL 복사</span>
          </div>
          <div className="line"></div>
        </div>
      )}

      {/* 닫기 → HomeTab 이동 */}
      <div className="checkout-footer-fixed" style={{ background: '#F6F6F6' }}>
        <div className="bottom-button" style={{ margin: 0 }}>
          <button onClick={goHome}>닫기</button>
        </div>
      </div>
    </div>
  );
}
